import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Button } from 'equal-ds-ui';
import { Eye, EyeOff } from 'lucide-react';
import type { AdminUserTableRow } from '../../../types/admin-users';

export interface AdminUserResetPasswordDialogProps {
  user: AdminUserTableRow | null;
  isOpen: boolean;
  onConfirm: (password: string) => Promise<void> | void;
  onClose: () => void;
  isProcessing?: boolean;
  /** Optional callback to show success message in parent (for backwards compatibility) */
  onSuccess?: (message: string) => void;
}

// Password regex matching Angular implementation
const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[@$!%*?&])(?!.*\s).{8,}$/;

export const AdminUserResetPasswordDialog: React.FC<AdminUserResetPasswordDialogProps> = ({
  user,
  isOpen,
  onConfirm,
  onClose,
  isProcessing = false,
  onSuccess,
}) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<{ password?: string; confirmPassword?: string }>({});
  const [touched, setTouched] = useState<{ password: boolean; confirmPassword: boolean }>({
    password: false,
    confirmPassword: false,
  });
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      setPassword('');
      setConfirmPassword('');
      setErrors({});
      setTouched({ password: false, confirmPassword: false });
      setShowPassword(false);
      setShowConfirmPassword(false);
      setSuccessMessage(null);
      setErrorMessage(null);
    }
  }, [isOpen]);

  // Auto-close dialog after success message
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        onClose();
        // Optionally notify parent
        if (onSuccess) {
          onSuccess(successMessage);
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, onClose, onSuccess]);

  // Validate password on blur
  const validatePassword = useCallback((value: string) => {
    if (!value) {
      return 'Password is required.';
    }
    if (!passwordRegex.test(value)) {
      return 'Password must contain at least 8 characters, including uppercase, lowercase, number and special character.';
    }
    return undefined;
  }, []);

  // Validate confirm password
  const validateConfirmPassword = useCallback((value: string, passwordValue: string) => {
    if (!value) {
      return 'Confirm password is required.';
    }
    if (value !== passwordValue) {
      return 'Passwords do not match.';
    }
    return undefined;
  }, []);

  // Handle password change
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);

    // Re-validate confirm password if it's been touched
    if (touched.confirmPassword && confirmPassword) {
      const confirmError = validateConfirmPassword(confirmPassword, value);
      setErrors((prev) => ({ ...prev, confirmPassword: confirmError }));
    }
  };

  // Handle password blur
  const handlePasswordBlur = () => {
    setTouched((prev) => ({ ...prev, password: true }));
    const error = validatePassword(password);
    setErrors((prev) => ({ ...prev, password: error }));
  };

  // Handle confirm password change
  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
  };

  // Handle confirm password blur
  const handleConfirmPasswordBlur = () => {
    setTouched((prev) => ({ ...prev, confirmPassword: true }));
    const error = validateConfirmPassword(confirmPassword, password);
    setErrors((prev) => ({ ...prev, confirmPassword: error }));
  };

  // Check if form is valid (using useMemo since this is a derived value, not a callback)
  const isFormValid = useMemo(() => {
    const passwordError = validatePassword(password);
    const confirmError = validateConfirmPassword(confirmPassword, password);
    return !passwordError && !confirmError;
  }, [password, confirmPassword, validatePassword, validateConfirmPassword]);

  // Handle form submit
  const handleSubmit = async () => {
    // Validate all fields
    const passwordError = validatePassword(password);
    const confirmError = validateConfirmPassword(confirmPassword, password);

    setErrors({ password: passwordError, confirmPassword: confirmError });
    setTouched({ password: true, confirmPassword: true });
    setErrorMessage(null);

    if (passwordError || confirmError) {
      return;
    }

    try {
      await onConfirm(password);
      setSuccessMessage(`Password changed successfully for ${user?.name ?? 'user'}.`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to reset password. Please try again.';
      setErrorMessage(message);
    }
  };

  if (!isOpen || !user) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-black/35 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative z-10 w-full max-w-md bg-white rounded-xl shadow-xl border border-border-default p-6 space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-text-primary">
            Reset Password
          </h3>
          <p className="text-sm text-text-secondary mt-1">
            Set a new password for{' '}
            <span className="font-semibold text-text-primary">{user.name}</span>
          </p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg px-4 py-3 text-sm">
            {successMessage}
          </div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-3 text-sm">
            {errorMessage}
          </div>
        )}

        <div className="bg-background-tertiary border border-border-default rounded-lg px-4 py-3 text-sm text-text-secondary">
          <div>
            <span className="font-medium text-text-primary">Email:</span> {user.email}
          </div>
          <div>
            <span className="font-medium text-text-primary">Employee ID:</span> {user.empId}
          </div>
        </div>

        {/* Password Field */}
        <div>
          <label className="block text-sm font-semibold text-text-secondary mb-2">
            New Password <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={handlePasswordChange}
              onBlur={handlePasswordBlur}
              placeholder="Enter new password"
              className={`w-full px-3 py-2 pr-10 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                touched.password && errors.password ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {touched.password && errors.password && (
            <p className="text-xs text-red-500 mt-1">{errors.password}</p>
          )}
          <p className="text-xs text-text-tertiary mt-1">
            Min 8 chars with uppercase, lowercase, number & special character (@$!%*?&)
          </p>
        </div>

        {/* Confirm Password Field */}
        <div>
          <label className="block text-sm font-semibold text-text-secondary mb-2">
            Confirm Password <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              onBlur={handleConfirmPasswordBlur}
              placeholder="Confirm new password"
              className={`w-full px-3 py-2 pr-10 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                touched.confirmPassword && errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary"
            >
              {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {touched.confirmPassword && errors.confirmPassword && (
            <p className="text-xs text-red-500 mt-1">{errors.confirmPassword}</p>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isProcessing}
            className="bg-white text-text-primary border border-border-default hover:bg-background-tertiary"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isProcessing || !isFormValid}
          >
            {isProcessing ? 'Resetting...' : 'Reset Password'}
          </Button>
        </div>
      </div>
    </div>
  );
};
